/**
 * AddToSegmentDropdown Component
 * Dropdown menu for adding selected contacts to a static segment
 */

import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getStaticSegments,
  addContactsToSegment,
} from "../../services/segments.service";
import { useToast } from "../ui/Toast";
import { useAuth } from "../../contexts/AuthContext";
import type { UUID } from "../../types/common.types";
import { QUERY_KEYS } from "../../utils/constants";

interface AddToSegmentDropdownProps {
  selectedContactIds: UUID[];
  onSuccess?: () => void;
  disabled?: boolean;
}

export default function AddToSegmentDropdown({
  selectedContactIds,
  onSuccess,
  disabled = false,
}: AddToSegmentDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch static segments — refetch every time dropdown opens
  const { data: segments = [], isLoading } = useQuery({
    queryKey: [QUERY_KEYS.SEGMENTS, "static"],
    queryFn: getStaticSegments,
    enabled: isOpen,
    staleTime: 0,
  });

  // Add to segment mutation
  const addMutation = useMutation({
    mutationFn: async (segmentId: UUID) => {
      await addContactsToSegment(segmentId, selectedContactIds, user?.id);
    },
    onSuccess: () => {
      showToast(
        "success",
        `Added ${selectedContactIds.length} contact${selectedContactIds.length !== 1 ? "s" : ""} to segment`,
      );
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SEGMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LISTS] }); // Also invalidate lists for backwards compat
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error adding contacts to segment:", error);
      showToast("error", "Failed to add contacts to segment");
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSegmentSelect = (segmentId: UUID) => {
    addMutation.mutate(segmentId);
  };

  const buttonDisabled =
    disabled || selectedContactIds.length === 0 || addMutation.isPending;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={buttonDisabled}
        className={`px-4 py-2 text-sm font-medium rounded-md border flex items-center gap-2 ${
          buttonDisabled
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add to Segment
        {selectedContactIds.length > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
            {selectedContactIds.length}
          </span>
        )}
        <svg
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                Loading segments...
              </div>
            ) : segments.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No static segments found.
                <br />
                <span className="text-xs">Create a static segment first.</span>
              </div>
            ) : (
              <>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b">
                  Select a segment
                </div>
                {segments.map((segment) => (
                  <button
                    key={segment.id}
                    onClick={() => handleSegmentSelect(segment.id)}
                    disabled={addMutation.isPending}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    <div className="font-medium">{segment.name}</div>
                    <div className="text-xs text-gray-500">
                      {segment.contact_count || 0} contacts
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
